import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  FormControlLabel,
  Switch,
  MenuItem,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import api from '../../api/axios';
import BlogContentEditor from '../../components/marketing/BlogContentEditor';
import BlogPostService, { BlogLocale, BlogPostPayload } from '../../services/blog-post.service';
import { absolutizeContentHtml, relativizeContentHtml } from '../../utils/strapiUpload';

function slugify(input: string) {
  return input
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\u0600-\u06FF-]/gi, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

async function uploadCover(file: File): Promise<number | null> {
  const formData = new FormData();
  formData.append('files', file);
  try {
    const response = await api.post('/upload', formData);
    if (response.data && response.data[0]) {
      return response.data[0].id;
    }
  } catch (error) {
    console.error('Upload failed', error);
  }
  return null;
}

const BlogPostForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);

  const basedOn = searchParams.get('basedOn');
  const targetLocale = (searchParams.get('locale') as BlogLocale | null) || null;

  const [locale, setLocale] = useState<BlogLocale>(
    targetLocale || (location.state as { locale?: BlogLocale } | null)?.locale || 'en',
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [published, setPublished] = useState(false);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [existingCoverId, setExistingCoverId] = useState<number | null>(null);

  const [form, setForm] = useState<BlogPostPayload>({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
  });

  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    const loc = (location.state as { locale?: BlogLocale } | null)?.locale || locale;

    BlogPostService.getOne(id, loc)
      .then((res) => {
        if (cancelled) return;
        const attrs = res.data?.data?.attributes;
        if (!attrs) return;
        setPublished(Boolean(attrs.publishedAt));
        setForm({
          title: attrs.title || '',
          slug: attrs.slug || '',
          excerpt: attrs.excerpt ?? '',
          content: absolutizeContentHtml(attrs.content ?? ''),
          seoTitle: attrs.seoTitle ?? '',
          seoDescription: attrs.seoDescription ?? '',
          seoKeywords: attrs.seoKeywords ?? '',
        });
        const coverId =
          attrs.cover?.data?.id ??
          (typeof attrs.cover === 'number' ? attrs.cover : null);
        setExistingCoverId(typeof coverId === 'number' ? coverId : null);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [id, isEdit]); // intentionally minimal — locale synced from state on first load

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let coverId: number | null | undefined = existingCoverId;
      if (coverFile) {
        const uploaded = await uploadCover(coverFile);
        if (!uploaded) {
          setError(t('marketing.blog.uploadFailed'));
          setLoading(false);
          return;
        }
        coverId = uploaded;
      }

      const payload = { ...form, content: relativizeContentHtml(form.content || '') };

      if (basedOn && !isEdit) {
        await BlogPostService.createLocalization(basedOn, (targetLocale || locale) as BlogLocale, payload, published);
      } else if (isEdit && id) {
        await BlogPostService.update(id, locale, payload, published, coverId);
      } else {
        await BlogPostService.create(locale, payload, published, coverId);
      }

      navigate('/marketing/blog');
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message;
      setError(typeof msg === 'string' ? msg : t('marketing.blog.saveError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 960 }}>
      <Typography variant="h4" gutterBottom>
        {basedOn ? t('marketing.blog.addLocalization') : isEdit ? t('marketing.blog.editPost') : t('marketing.blog.newPost')}
      </Typography>

      { basedOn && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('marketing.blog.basedOnHint', { id: basedOn })}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {!basedOn && (
              <TextField select label={t('marketing.blog.locale')} value={locale} onChange={(e) => setLocale(e.target.value as BlogLocale)} required>
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="ar">العربية</MenuItem>
              </TextField>
            )}
            {basedOn && (
              <TextField label={t('marketing.blog.locale')} value={targetLocale || locale} disabled />
            )}

            <TextField
              name="title"
              label={t('marketing.blog.articleTitle')}
              value={form.title}
              onChange={handleChange}
              required
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField name="slug" label={t('marketing.blog.slugField')} value={form.slug} onChange={handleChange} required fullWidth helperText={t('marketing.blog.slugHint')} />
              {!basedOn && (
                <Button
                  variant="outlined"
                  onClick={() => setForm((f) => ({ ...f, slug: slugify(f.title || '') || f.slug }))}
                  sx={{ alignSelf: { sm: 'center' }, mt: { sm: '8px !important' } }}
                >
                  {t('marketing.blog.generateSlug')}
                </Button>
              )}
            </Stack>

            <TextField name="excerpt" label={t('marketing.blog.excerpt')} value={form.excerpt || ''} onChange={handleChange} fullWidth multiline rows={3} />

            <BlogContentEditor
              label={t('marketing.blog.content')}
              value={form.content || ''}
              onChange={(content) => setForm((prev) => ({ ...prev, content }))}
              uploadErrorText={t('marketing.blog.uploadFailed')}
            />

            <Typography variant="subtitle2" sx={{ pt: 1 }}>
              {t('marketing.blog.seoSection')}
            </Typography>
            <TextField name="seoTitle" label={t('marketing.blog.metaTitle')} value={form.seoTitle || ''} onChange={handleChange} fullWidth />
            <TextField name="seoDescription" label={t('marketing.blog.metaDescription')} value={form.seoDescription || ''} onChange={handleChange} fullWidth multiline rows={3} />
            <TextField name="seoKeywords" label={t('marketing.blog.metaKeywords')} value={form.seoKeywords || ''} onChange={handleChange} fullWidth helperText={t('marketing.blog.keywordsHint')} />

            <Button variant="outlined" component="label">
              {t('marketing.blog.coverImage')}
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setCoverFile(f || null);
                }}
              />
            </Button>
            {coverFile ? (
              <Typography variant="caption">{coverFile.name}</Typography>
            ) : existingCoverId ? (
              <Typography variant="caption" color="text.secondary">
                {t('marketing.blog.keepExistingCover')}
              </Typography>
            ) : null}

            <FormControlLabel
              control={<Switch checked={published} onChange={(e) => setPublished(e.target.checked)} />}
              label={t('marketing.blog.publishImmediately')}
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" startIcon={<CancelIcon />} onClick={() => navigate('/marketing/blog')}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="contained" disabled={loading} startIcon={<SaveIcon />}>
                {loading ? t('common.loading') : t('common.save')}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default BlogPostForm;
